import React, { useRef, useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

/**
 * Single-input segmented time picker.
 * Shows "HH:MM" — click on hour or minute portion to select that segment.
 * Only numeric keys accepted. Colon is fixed and not editable.
 */

type Segment = 'hour' | 'minute';

interface DropdownOption {
  value: string;
  label: string;
  duration: string;
}

interface TimeFieldProps {
  value: string; // HH:MM (24h)
  onChange: (time: string) => void;
  enableDropdown?: boolean;
  dropdownOptions?: DropdownOption[];
  onDropdownSelect?: (time: string) => void;
}

const TimeField: React.FC<TimeFieldProps> = ({
  value,
  onChange,
  enableDropdown,
  dropdownOptions,
  onDropdownSelect,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [segment, setSegment] = useState<Segment | null>(null);
  const [buf, setBuf] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number } | null>(null);

  const [h, m] = value.split(':').map(Number);
  const display = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;

  // Determine which segment was clicked based on cursor position
  const detectSegment = (pos: number): Segment => (pos <= 2 ? 'hour' : 'minute');

  // Highlight the segment text without resetting buffer
  const highlightSegment = useCallback((seg: Segment) => {
    requestAnimationFrame(() => {
      if (seg === 'hour') {
        inputRef.current?.setSelectionRange(0, 2);
      } else {
        inputRef.current?.setSelectionRange(3, 5);
      }
    });
  }, []);

  const selectSegment = useCallback((seg: Segment) => {
    setSegment(seg);
    setBuf('');
    highlightSegment(seg);
  }, [highlightSegment]);

  const openDropdown = useCallback(() => {
    if (!containerRef.current || !enableDropdown) return;
    const rect = containerRef.current.getBoundingClientRect();
    setDropdownPos({ top: rect.bottom + 4, left: rect.right - 160 });
    setDropdownOpen(true);
  }, [enableDropdown]);

  const closeAll = useCallback(() => {
    setDropdownOpen(false);
    setSegment(null);
    setBuf('');
  }, []);

  // Commit typed buffer to the current segment
  const commitBuf = useCallback((currentBuf: string, seg: Segment) => {
    if (!currentBuf) return;
    let n = parseInt(currentBuf, 10);
    if (isNaN(n) || n < 0) n = 0;
    if (seg === 'hour') {
      if (n > 23) n = 23;
      onChange(`${String(n).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    } else {
      if (n > 59) n = 59;
      onChange(`${String(h).padStart(2, '0')}:${String(n).padStart(2, '0')}`);
    }
    setBuf('');
  }, [onChange, h, m]);

  const handleMouseUp = () => {
    // Ensure the input has focus (modal drag handlers may interfere)
    inputRef.current?.focus();
    const pos = inputRef.current?.selectionStart ?? 0;
    const seg = detectSegment(pos);
    selectSegment(seg);
    if (enableDropdown) openDropdown();
  };

  const handleFocus = () => {
    // Default to hour segment on focus
    if (!segment) selectSegment('hour');
    if (enableDropdown) openDropdown();
  };

  const handleBlur = () => {
    if (segment && buf) commitBuf(buf, segment);
    // Delay to allow dropdown click to register
    setTimeout(() => {
      if (!containerRef.current?.contains(document.activeElement)) {
        closeAll();
      }
    }, 150);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const seg = segment || 'hour';

    if (e.key === 'Tab') {
      if (seg === 'hour') {
        e.preventDefault();
        commitBuf(buf, 'hour');
        selectSegment('minute');
      } else {
        commitBuf(buf, 'minute');
        closeAll();
        // let natural tab proceed
      }
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      commitBuf(buf, seg);
      inputRef.current?.blur();
      closeAll();
      return;
    }

    if (e.key === 'Escape') {
      e.preventDefault();
      setBuf('');
      inputRef.current?.blur();
      closeAll();
      return;
    }

    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      if (seg === 'minute') {
        commitBuf(buf, 'minute');
        selectSegment('hour');
      }
      return;
    }

    if (e.key === 'ArrowRight') {
      e.preventDefault();
      if (seg === 'hour') {
        commitBuf(buf, 'hour');
        selectSegment('minute');
      }
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (seg === 'hour') {
        onChange(`${String((h + 1) % 24).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
      } else {
        onChange(`${String(h).padStart(2, '0')}:${String((m + 1) % 60).padStart(2, '0')}`);
      }
      requestAnimationFrame(() => selectSegment(seg));
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (seg === 'hour') {
        onChange(`${String((h - 1 + 24) % 24).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
      } else {
        onChange(`${String(h).padStart(2, '0')}:${String((m - 1 + 60) % 60).padStart(2, '0')}`);
      }
      requestAnimationFrame(() => selectSegment(seg));
      return;
    }

    if (/^\d$/.test(e.key)) {
      e.preventDefault();
      const newBuf = buf + e.key;
      setBuf(newBuf);

      if (newBuf.length >= 2) {
        commitBuf(newBuf, seg);
        if (seg === 'hour') {
          // Auto-advance to minute
          requestAnimationFrame(() => selectSegment('minute'));
        } else {
          // Done editing
          requestAnimationFrame(() => {
            inputRef.current?.blur();
            closeAll();
          });
        }
      } else {
        // Show buffer in the segment while typing
        highlightSegment(seg);
      }
      return;
    }

    if (e.key === 'Backspace') {
      e.preventDefault();
      setBuf(buf.slice(0, -1));
      return;
    }

    // Block all other keys
    e.preventDefault();
  };

  // Close dropdown on outside click
  useEffect(() => {
    if (!dropdownOpen) return;
    const handler = (e: MouseEvent) => {
      const portal = document.getElementById('time-dropdown-portal');
      if (
        containerRef.current && !containerRef.current.contains(e.target as Node) &&
        (!portal || !portal.contains(e.target as Node))
      ) {
        closeAll();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [dropdownOpen, closeAll]);

  // Build display value — show buffer in active segment
  let displayValue = display;
  if (buf && segment) {
    const padded = buf.padEnd(2, ' ');
    if (segment === 'hour') {
      displayValue = `${padded}:${display.slice(3)}`;
    } else {
      displayValue = `${display.slice(0, 3)}${padded}`;
    }
  }

  return (
    <div ref={containerRef} className="inline-flex items-center flex-shrink-0" data-no-drag>
      <input
        ref={inputRef}
        type="text"
        inputMode="numeric"
        value={displayValue}
        onClick={handleMouseUp}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onChange={() => {}} // controlled via keydown
        className="w-[4.5rem] text-center px-2.5 py-1.5 bg-[var(--input-bg)] rounded-lg text-xs text-[var(--content-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--accent-primary)]"
        style={{ caretColor: 'transparent' }}
        data-no-drag
      />

      {/* Portal dropdown for end time */}
      {dropdownOpen && enableDropdown && dropdownOptions && dropdownPos && createPortal(
        <div
          id="time-dropdown-portal"
          data-tag-input-portal
          className="fixed z-[600] bg-[var(--surface-elevated)] border border-[var(--border-primary)] rounded-lg overflow-y-auto scrollbar-styled animate-slideDown"
          style={{
            top: dropdownPos.top,
            left: Math.max(8, dropdownPos.left),
            maxHeight: '200px',
            minWidth: '160px',
            boxShadow: 'var(--shadow-lg)',
          }}
        >
          {dropdownOptions.map((opt) => (
            <div
              key={opt.value}
              onMouseDown={(e) => {
                e.preventDefault();
                onDropdownSelect?.(opt.value);
                closeAll();
              }}
              className={`h-8 px-3 flex items-center justify-between cursor-pointer text-xs ${
                opt.value === value
                  ? 'bg-[var(--accent-subtle)] text-[var(--accent-primary)] font-medium'
                  : 'text-[var(--content-primary)] hover:bg-[var(--sidebar-hover)]'
              }`}
            >
              <span>{opt.label}</span>
              <span className="text-[var(--content-tertiary)] ml-3">{opt.duration}</span>
            </div>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};

// ── Public components ──

interface InlineTimeInputProps {
  value: string; // HH:MM
  onChange: (time: string) => void;
  timeFormat: '12h' | '24h'; // kept for API compat
}

export const InlineTimeInput: React.FC<InlineTimeInputProps> = ({ value, onChange }) => {
  return <TimeField value={value} onChange={onChange} />;
};

interface EndTimeInputProps {
  value: string; // HH:MM
  startTime: string; // HH:MM
  onChange: (time: string) => void;
  onNextDay?: () => void;
  timeFormat: '12h' | '24h';
  stepMinutes?: number;
  maxOffsetMinutes?: number;
}

const durationLabel = (startMin: number, endMin: number): string => {
  let diff = endMin - startMin;
  if (diff <= 0) diff += 24 * 60;
  const hrs = Math.floor(diff / 60);
  const mins = diff % 60;
  if (hrs > 0 && mins > 0) return `${hrs} hr ${mins} min`;
  if (hrs > 0) return `${hrs} hr`;
  return `${mins} min`;
};

const fmtHHMM = (h: number, m: number): string =>
  `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;

export const EndTimeInput: React.FC<EndTimeInputProps> = ({ value, startTime, onChange, onNextDay, stepMinutes = 30, maxOffsetMinutes = 180 }) => {
  const [sH, sM] = startTime.split(':').map(Number);
  const startTotal = sH * 60 + sM;

  const options: DropdownOption[] = [];
  for (let offset = stepMinutes; offset <= maxOffsetMinutes; offset += stepMinutes) {
    const totalMin = (startTotal + offset) % (24 * 60);
    const oh = Math.floor(totalMin / 60);
    const om = totalMin % 60;
    const tv = fmtHHMM(oh, om);
    options.push({
      value: tv,
      label: tv,
      duration: durationLabel(startTotal, totalMin),
    });
  }

  const handleChange = useCallback((newTime: string) => {
    const [eH, eM] = newTime.split(':').map(Number);
    const endTotal = eH * 60 + eM;
    if (endTotal <= startTotal && onNextDay) {
      onNextDay();
    }
    onChange(newTime);
  }, [onChange, onNextDay, startTotal]);

  return (
    <TimeField
      value={value}
      onChange={handleChange}
      enableDropdown
      dropdownOptions={options}
      onDropdownSelect={handleChange}
    />
  );
};
